import {
  DocsCallout,
  DocsContent,
  DocsDiagram,
  DocsLayout,
  DocsLink,
  H2,
  H3,
} from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsPipelinesGeospatial() {
  return (
    <DocsLayout>
      <DocsContent
        title="Geospatial Pipeline"
        description="How XENDREL processes location tracking data — GPS trackers, cell tower CDRs, AIS ship tracking — using a hybrid storage model optimized for time-series geospatial data."
        previousPage={{ title: 'Tabular Extraction', href: '/docs/pipelines/tabular' }}
        nextPage={{ title: 'Graph Explorer', href: '/docs/graph-explorer' }}
      >
        <H2 id="overview">Overview</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Geospatial pipeline is designed for time-series location data — files where each row
          represents an observation of an entity (vehicle, phone, vessel) at a specific place and
          time. Unlike the Tabular pipeline which writes every row to Neo4j, the Geospatial pipeline
          uses a <strong className="text-foreground">hybrid storage model</strong> that keeps bulk
          observation data in optimized Parquet files while extracting only unique entities to the
          knowledge graph.
        </p>

        <DocsCallout variant="info" title="Why Not Neo4j for Track Points?">
          A GPS tracker recording once per minute generates 1,440 points per day per vehicle.
          Storing each as a Neo4j node with two relationships (entity link + location link) means
          4,320 graph elements per vehicle per day. For 10 vehicles over 30 days, that is 1.3
          million nodes and relationships — causing severe query performance degradation. The
          Geospatial pipeline solves this by keeping observations in columnar Parquet files,
          queryable in milliseconds via DuckDB.
        </DocsCallout>

        <H2 id="supported-formats">Supported Data Formats</H2>

        <div className="space-y-4 my-6">
          <FormatCard
            format="Vehicle GPS Tracker"
            description="Covert or fleet GPS tracker exports. Each row is a position fix with coordinates, speed, heading, and timestamp."
            columns="vehicle_id, plate, latitude, longitude, speed, heading, timestamp"
            entityType="Vehicle"
          />
          <FormatCard
            format="Cell Tower CDR (Call Detail Records)"
            description="Telecom provider records showing calls, SMS, and data sessions with the cell tower that handled each event. Position is approximate (tower location + sector azimuth)."
            columns="msisdn, imsi, event_type, start_time, duration, other_party, tower_lat, tower_lon, azimuth"
            entityType="Phone"
          />
          <FormatCard
            format="AIS Ship Tracking"
            description="Automatic Identification System data for maritime vessel tracking. Standard format from vessel transponders."
            columns="mmsi, vessel_name, lat, lon, speed_over_ground, course, timestamp, nav_status"
            entityType="Vessel"
          />
          <FormatCard
            format="Generic Location Log"
            description="Any CSV with entity identifier, coordinates, and timestamps — surveillance logs, check-in records, ANPR camera sightings."
            columns="entity_id, name, latitude, longitude, timestamp, notes"
            entityType="Configurable"
          />
        </div>

        <H2 id="architecture">Hybrid Storage Architecture</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Geospatial pipeline splits data into two storage tiers based on what each is optimized
          for:
        </p>

        <div className="my-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-950/20">
            <div className="font-semibold text-blue-600 dark:text-blue-400 mb-2">
              Neo4j — Entities
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Unique entities extracted from the data: vehicles, phones, people, cell towers. Few
              nodes, rich relationships.
            </p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>Vehicle (plate, make, color)</li>
              <li>Phone (MSISDN, IMSI, IMEI)</li>
              <li>CellTower (cell_id, lat, lon)</li>
              <li>Person (name, identifiers)</li>
              <li>Relationships: CONTACTED, REGISTERED_TO, HAS_PHONE</li>
            </ul>
          </div>
          <div className="p-4 rounded-lg border border-green-500/30 bg-green-950/20">
            <div className="font-semibold text-green-600 dark:text-green-400 mb-2">
              Remapped Parquet — Observations
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              All track points, CDR events, and position fixes. Thousands to millions of rows,
              queried by DuckDB at read time.
            </p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>entity_id, entity_type, entity_name</li>
              <li>latitude, longitude, timestamp</li>
              <li>metadata (speed, heading, event_type, ...)</li>
              <li>Queried with time-range + spatial filters</li>
              <li>No Neo4j overhead — fast aggregation</li>
            </ul>
          </div>
        </div>

        <H2 id="data-flow">Data Flow</H2>

        <DocsDiagram>{`GPS/CDR/AIS CSV File
    ↓
Phase 1 (unchanged):
    ├─ Parse CSV → raw Parquet on S3
    ├─ Detect "geospatial" data pattern
    └─ Column analysis + sample rows
    ↓
Phase 2 — Geospatial Pipeline:
    ├─ Auto-Mapping (LLM):
    │   ├─ Identifies entity columns (vehicle_id, msisdn, mmsi)
    │   ├─ Identifies geo columns (latitude, longitude)
    │   ├─ Identifies time column (timestamp)
    │   └─ Identifies metadata columns (speed, heading, event_type)
    │
    ├─ GeoParquetMapper:
    │   ├─ Reads raw Parquet from Phase 1
    │   ├─ Renames columns → normalized schema
    │   └─ Writes .geo.parquet to S3
    │
    └─ GeoEntityExtractor:
        ├─ SELECT DISTINCT entities from .geo.parquet
        ├─ MERGE unique entities to Neo4j
        └─ Create derived relationships (Phone→CONTACTED→Phone)
    ↓
API Layer:
    ├─ Entities from Neo4j (sidebar, graph)
    └─ Track points from DuckDB on .geo.parquet (map, timeline)`}</DocsDiagram>

        <H2 id="column-mapping">Column Mapping</H2>

        <H3 id="auto-mapping">Automatic Mapping</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          When a geospatial CSV enters Phase 2, the analyzer examines column headers and sample data
          to propose a <strong className="text-foreground">GeoMappingConfig</strong>:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity type</strong> — What kind of entity each
              row tracks (vehicle, phone, vessel, person)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity identifier column</strong> — Which column
              uniquely identifies each entity (vehicle_id, msisdn, mmsi)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity name column</strong> — Display name for the
              entity (plate number, phone number, vessel name)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Latitude / Longitude columns</strong> — Coordinate
              fields (WGS84)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Timestamp column</strong> — Time of observation
              (ISO 8601 or custom format)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Metadata columns</strong> — Additional data
              carried into the .geo.parquet (speed, heading, azimuth, notes)
            </span>
          </li>
        </ul>

        <H3 id="user-override">User Review and Override</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          The auto-proposed mapping is presented for review. You can modify any column assignment,
          change the entity type, or add derived entities and relationships before confirming.
        </p>

        <DocsCallout variant="tip" title="Derived Entities and Relationships">
          For CDR data, the mapper can derive secondary entities automatically. For example, the{' '}
          <code className="text-amber-600 dark:text-amber-400">other_party</code> column in a CDR
          file represents the phone number that was called or texted — the pipeline creates a second
          Phone entity and a CONTACTED relationship in Neo4j.
        </DocsCallout>

        <H2 id="remapped-parquet">Remapped Parquet Schema</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Regardless of the original CSV format, the{' '}
          <strong className="text-foreground">GeoParquetMapper</strong> produces a normalized{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">
            .geo.parquet
          </code>{' '}
          file with a standard schema:
        </p>

        <div className="my-6 overflow-x-auto">
          <table className="w-full wrap-normal text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-muted-foreground font-medium">Column</th>
                <th className="text-left py-2 px-3 text-muted-foreground font-medium">Type</th>
                <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  entity_id
                </td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">Unique entity identifier</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  entity_type
                </td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">"vehicle", "phone", "vessel", "person"</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  entity_name
                </td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">Display name (plate, MSISDN, vessel name)</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">latitude</td>
                <td className="py-2 px-3">FLOAT64</td>
                <td className="py-2 px-3">WGS84 latitude</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  longitude
                </td>
                <td className="py-2 px-3">FLOAT64</td>
                <td className="py-2 px-3">WGS84 longitude</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  timestamp
                </td>
                <td className="py-2 px-3">TIMESTAMP</td>
                <td className="py-2 px-3">UTC observation time</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">metadata</td>
                <td className="py-2 px-3">JSON</td>
                <td className="py-2 px-3">All other mapped columns as JSON object</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  source_file
                </td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">Original filename for provenance</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">case_id</td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">Case UUID for multi-tenant isolation</td>
              </tr>
              <tr>
                <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                  document_id
                </td>
                <td className="py-2 px-3">STRING</td>
                <td className="py-2 px-3">Source document UUID</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-foreground mb-4 leading-relaxed">
          This normalization means the API and frontend always work with the same schema, regardless
          of whether the source was a vehicle tracker, CDR export, or ship transponder log.
        </p>

        <H2 id="examples">Examples</H2>

        <H3 id="vehicle-surveillance">Vehicle Surveillance CSV</H3>

        <Card className="my-4 rounded-lg bg-card/50 p-4 font-mono text-xs text-foreground overflow-x-auto">
          <pre className="whitespace-pre">{`Original CSV:
  vehicle_id, vehicle_alias, plate_number, latitude,  longitude, speed_kmh, heading, timestamp,              note
  VEH-001,   ALFA,          4A2 3311,     50.07854,  14.41893,  0,         0,       2026-03-15T07:45:00Z,   Engine start

GeoMappingConfig:
  entity_type: "vehicle"
  entity_identifier: "vehicle_id"
  entity_name: "vehicle_alias"
  lat: "latitude", lon: "longitude"
  time: "timestamp"
  metadata: speed_kmh, heading, plate_number, note

Remapped .geo.parquet row:
  entity_id=VEH-001, entity_type=vehicle, entity_name=ALFA
  latitude=50.07854, longitude=14.41893, timestamp=2026-03-15T07:45:00Z
  metadata={"speed_kmh": 0, "heading": 0, "plate_number": "4A2 3311", "note": "Engine start"}

Neo4j (entities only):
  (:Vehicle {_key: "VEH-001", name: "ALFA", plate: "4A2 3311", case_id: "..."})`}</pre>
        </Card>

        <H3 id="cdr-example">Cell Tower CDR</H3>

        <Card className="my-4 rounded-lg bg-card/50 p-4 font-mono text-xs text-foreground overflow-x-auto">
          <pre className="whitespace-pre">{`Original CSV:
  msisdn,       imsi,             event_type, start_time,           other_party,  tower_lat, tower_lon, azimuth
  420776551234, 230021234500001,  CALL_OUT,   2026-03-15T07:45:12Z, 420608997001, 50.07830,  14.41920,  120

GeoMappingConfig:
  entity_type: "phone"
  entity_identifier: "msisdn"
  entity_name: "msisdn"
  lat: "tower_lat", lon: "tower_lon"
  time: "start_time"
  metadata: imsi, event_type, other_party, azimuth
  derived_entities: [{type: "phone", from: "other_party"}]
  derived_relationships: [{type: "CONTACTED", source: "msisdn", target: "other_party"}]

Neo4j:
  (:Phone {_key: "420776551234", msisdn: "420776551234", imsi: "230021234500001"})
  (:Phone {_key: "420608997001", msisdn: "420608997001"})
  (420776551234)-[:CONTACTED]->(420608997001)`}</pre>
        </Card>

        <H2 id="api-queries">API Query Layer</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The frontend queries geospatial data through dedicated API endpoints that use DuckDB to
          read{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">
            .geo.parquet
          </code>{' '}
          files directly:
        </p>

        <div className="space-y-4 my-6">
          <EndpointCard
            method="GET"
            path="/cases/{case_id}/geolocation/entities"
            source="Neo4j"
            description="Returns unique entities with their types, names, and relationships. Powers the entity sidebar."
          />
          <EndpointCard
            method="GET"
            path="/cases/{case_id}/geolocation/tracks"
            source="DuckDB"
            description="Returns track points filtered by entity_id, time range (from/to), with full metadata. Powers the map visualization and timeline."
            params="entity_id, from, to"
          />
          <EndpointCard
            method="GET"
            path="/cases/{case_id}/geolocation/events"
            source="DuckDB"
            description="Returns discrete events (calls, SMS, stops, speed changes) within a time range. Powers the timeline event markers."
            params="from, to, event_type"
          />
          <EndpointCard
            method="GET"
            path="/cases/{case_id}/geolocation/convergences"
            source="DuckDB"
            description="Detects co-locations — when two different entities are within a distance threshold during a time window. Uses spatial + temporal cross-join."
            params="distance_m (default 500), time_window_min (default 20)"
          />
        </div>

        <H2 id="frontend-visualization">Frontend Visualization</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Geospatial data is displayed on a Cesium 3D globe with:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity markers</strong> — Color-coded pins for
              each tracked entity
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Movement tracks</strong> — Polylines showing the
              path of each entity over time
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>
                <strong className="text-foreground">Custom timeline</strong> — Scrubber with
                playback controls, showing entity positions at any point in time
              </span>
              <DocsLink
                to="/docs/timeline-view"
                hint="The Timeline view combines this scrubber with the knowledge graph and events list. Its playhead also drives the Cesium clock, so tracks animate in lock-step."
              />
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Event markers</strong> — Calls, SMS, stops, and
              convergences displayed on the timeline
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Convergence alerts</strong> — Visual indicators
              when tracked entities meet at the same location
            </span>
          </li>
        </ul>

        <DocsCallout variant="tip" title="Multi-Source Overlay">
          You can upload multiple geospatial files to the same case — for example, vehicle GPS
          tracks and CDR records for the suspects' phones. Both datasets appear on the same map and
          timeline, allowing you to correlate vehicle movements with phone activity.
        </DocsCallout>

        <H2 id="best-practices">Best Practices</H2>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Timestamps must be consistent</strong> — Ensure
              all timestamps are in the same timezone (UTC preferred). Mixed timezones will cause
              incorrect track ordering and false convergence detections.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Verify coordinate format</strong> — Coordinates
              must be in decimal degrees (WGS84). DMS format (degrees/minutes/seconds) needs
              conversion.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">CDR locations are approximate</strong> — Cell
              tower positions show where the tower is, not the phone. The actual phone position
              could be anywhere within the cell coverage area (200m in urban, several km in rural).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Filter noisy data</strong> — GPS trackers
              sometimes report (0,0) or wildly inaccurate positions. The pipeline validates
              coordinates but review edge cases.
            </span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}

function FormatCard({
  format,
  description,
  columns,
  entityType,
}: {
  format: string
  description: string
  columns: string
  entityType: string
}) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-foreground">{format}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-muted text-amber-600 dark:text-amber-400">
          {entityType}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-2">{description}</p>
      <div className="text-xs font-mono text-muted-foreground">
        <span className="text-amber-500/70">Columns:</span> {columns}
      </div>
    </Card>
  )
}

function EndpointCard({
  method,
  path,
  source,
  description,
  params,
}: {
  method: string
  path: string
  source: string
  description: string
  params?: string
}) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2 md:flex-nowrap">
        <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-900/50 text-green-600 dark:text-green-400">
          {method}
        </span>
        <span className="order-last w-full break-all font-mono text-xs text-foreground md:order-none md:w-auto md:break-normal md:text-sm">
          {path}
        </span>
        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground ml-auto">
          {source}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
      {params && (
        <div className="text-xs font-mono text-muted-foreground mt-2">
          <span className="text-amber-500/70">Params:</span> {params}
        </div>
      )}
    </Card>
  )
}
